import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import VideosAdminPermissionsForm from '../components/videosAdminPermissionsForm';

import useVideoVersion from '../hooks/useVideoVersion';

import handleUpdatePermissions from '../helpers/handleUpdatePermissions';
import handleGroupNameClick from '../helpers/handleGroupNameClick';

import getEditorPermissionsSchema from '../schemas/getEditorPermissionsSchema';
import getLearnerPermissionsSchema from '../schemas/getLearnerPermissionsSchema';

const VideosAdminPermissionsFormContainer = ({
  module,
  isTopicPermissions,
  permissions,
  permissionsKey,
}) => {
  const { id, orgId, type } = useParams();

  const queryClient = useQueryClient();
  const { videoVersion } = useVideoVersion({ isEnabled: false });
  const { data, status } = permissions;

  const onHandleFormUpdate = useCallback(
    ({ update }) => {
      queryClient.setQueryData(permissionsKey, (oldProperties) => ({
        ...oldProperties,
        [module]: { ...oldProperties[module], ...update },
      }));
    },
    [module, permissionsKey],
  );

  const onAutoSave = useCallback(
    async (modifiedModel) => {
      try {
        await handleUpdatePermissions({
          modifiedModel,
          videoVersion,
          cache: data,
          type: isTopicPermissions ? 'topic' : 'video',
          module,
          params: { orgId, id, type },
        });
      } catch (error) {
        console.log(error);
      }
    },
    [isTopicPermissions, module, orgId],
  );

  const getSchemaMapping = useCallback(
    () => ({
      editor: () => getEditorPermissionsSchema({ orgId, onClick: handleGroupNameClick }),
      learner: () => getLearnerPermissionsSchema(),
    }),
    [orgId],
  );

  const schema = getSchemaMapping()[module]();
  const title = schema?.title;
  const body = schema?.body;

  const model = data[module];

  const shouldShowLoading = status !== 'success';

  return (
    <VideosAdminPermissionsForm
      title={title}
      body={body}
      shouldShowLoading={shouldShowLoading}
      schema={schema.schema}
      model={model}
      onUpdate={onHandleFormUpdate}
      onAutoSave={onAutoSave}
    />
  );
};

export default React.memo(VideosAdminPermissionsFormContainer);
